import { useIncognito } from '#pplx/frontend/apps/ask/incognito/useIncognito.js';
import { openSettings } from '#pplx/frontend/apps/ask/settings/settingsHash.js';
import { assertNever } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { useCallback } from 'react';
const LazyComputerFeedbackModal = Dynamic(async () => {
    const { ComputerFeedbackModal } = await import('#pplx/frontend/apps/ask/computer/feedback/ComputerFeedbackModal.js');
    return { default: ComputerFeedbackModal };
});
export function runAfterCurrentSelectionEvent(callback) {
    // Skill selection mutates Lexical by inserting a MentionNode. Running that
    // synchronously inside the active keyboard/pointer event can flatten the node
    // through its markdown-link text content before the input finishes handling
    // the event.
    queueMicrotask(callback);
}
/** Executes the platform-independent command-menu actions (slash commands and
 * UI actions); skill and workflow selection stays with the calling controller. */
export function useComputerCommandMenuActions({ getEditor, onMenuClose, openModal, isIncognito, onUiActionSelect, onOpenSettings, }) {
    const router = useRouter();
    const { toggleIncognito } = useIncognito();
    const handleSlashCommandSelect = useCallback((action, icon) => {
        const editor = getEditor();
        if (!editor)
            return;
        runAfterCurrentSelectionEvent(() => {
            editor.insertMention({
                uuid: action.commandName,
                variant: 'slash-command',
                text: action.commandName,
                queryText: action.prefix,
                slashCommandSubmittedLabel: action.submittedLabel,
                icon: { type: 'icon', icon },
            }, { replaceActiveCommandQuery: true });
            onMenuClose();
        });
    }, [onMenuClose, getEditor]);
    const handleUiActionSelect = useCallback((action) => {
        const editor = getEditor();
        const clearCommandQuery = () => {
            if (editor) {
                editor.clearActiveCommandQuery();
            }
        };
        runAfterCurrentSelectionEvent(() => {
            switch (action.actionId) {
                case 'switch_to_search_mode':
                    clearCommandQuery();
                    onUiActionSelect?.(action.actionId);
                    break;
                case 'open_computer_settings':
                    clearCommandQuery();
                    if (onOpenSettings) {
                        onOpenSettings();
                    }
                    else {
                        openSettings(router, '/computer');
                    }
                    break;
                case 'toggle_incognito':
                    clearCommandQuery();
                    toggleIncognito(isIncognito);
                    break;
                case 'open_feedback':
                    clearCommandQuery();
                    openModal(LazyComputerFeedbackModal, {}, { replace: true });
                    break;
                case 'open_model_selector':
                    clearCommandQuery();
                    onUiActionSelect?.(action.actionId);
                    return;
                case 'open_side_chat':
                case 'fork_session':
                    clearCommandQuery();
                    onUiActionSelect?.(action.actionId);
                    break;
                default:
                    assertNever(action.actionId);
                    return;
            }
            onMenuClose();
        });
    }, [
        isIncognito,
        onMenuClose,
        onOpenSettings,
        onUiActionSelect,
        openModal,
        router,
        getEditor,
        toggleIncognito,
    ]);
    return { handleSlashCommandSelect, handleUiActionSelect };
}
//# sourceMappingURL=useComputerCommandMenuActions.js.map